// Single-object refresh path used by Slice 22's DDL invalidator. After a
// `CREATE OR REPLACE VIEW` / `ALTER FUNCTION` / `DROP …` / etc. the
// invalidator re-runs a focused fetch + parse via this helper, which REPLACES
// the object's index records: it fetches FIRST and only then drops the old
// records, so a failed catalog read (`fetchRows` throws) leaves the previous
// records in place instead of turning a transient failure into "the object is
// gone". A null body — the query ran and found nothing — is a confirmed
// absence and removes the records.
//
// The full-connection builder lives in `indexBuilder.ts`; this file is the
// single-object equivalent. Keeping it separate keeps `indexBuilder.ts`
// under the 200-line budget.

import { databaseEngineIdToSqlEngine } from '@/features/intellisense/versionGating/engineIdMapping';
import type { DatabaseEngineId } from '@/core/engines';

import type { IdentifierIndex, ParseableObjectKind, PersistedObjectListing, QualifiedName } from '../types';

import {
  indexCheckConstraintListing,
  indexDefaultConstraintListing,
  indexInlineTvfListing,
  indexProceduralListing,
  indexScalarFunctionListing,
  indexViewListing,
} from './indexBuilder';
import { fetchObjectBody, fetchProceduralObjectBody } from './objectBodyFetcher';

export type BuildForObjectOptions = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly objectKind: ParseableObjectKind;
  readonly objectName: QualifiedName;
  /** NAV-5: when provided, called after the async body fetch. If it returns
   *  `true` the index registration has been torn down (disconnect mid-fetch)
   *  and the index must not be touched. */
  readonly isStale?: () => boolean;
  /** When provided, called immediately before the synchronous swap: `true`
   *  means a CONNECTION-WIDE build is filling this index right now (it started
   *  while the body fetch above was in flight). Writing then would record the
   *  object into an index the build — which only records, and which a retry
   *  EMPTIED first — is about to record it into as well, listing it twice.
   *  Reported as `'deferred'` so the caller re-queues behind the build instead
   *  of dropping the refresh. */
  readonly isBuilding?: () => boolean;
  /** When provided, called immediately before the synchronous swap: `true`
   *  means a LATER refresh of this SAME object has started since this one
   *  began, so this body is the older statement's and must not be written.
   *
   *  The two guards above cannot answer this. A refresh frees its queue slot
   *  BEFORE fetching (deliberately — see `indexInvalidator.ts`'s
   *  `runQueuedRefresh`), so two ALTERs of one object put two refreshes in
   *  flight at once, and nothing ordered their SWAPS: whichever CATALOG READ
   *  returned last won. A slow read for the older statement then installed the
   *  older body over the newer one, and Find References stayed wrong until the
   *  next DDL touched the object. Reported as `'superseded'` — the newer
   *  refresh is already doing this one's work, so there is nothing to re-queue. */
  readonly isSuperseded?: () => boolean;
};

/** What a refresh did to the object's records:
 *  - `indexed`  — the catalog returned a body; old records replaced by new ones.
 *  - `absent`   — the catalog ran and holds no such object; old records removed.
 *  - `failed`   — the fetch failed, the engine has no parser mapping, or the
 *                 registration went stale mid-fetch; records left untouched.
 *  - `deferred` — a connection-wide build was in flight at the swap
 *                 ({@link BuildForObjectOptions.isBuilding}); NOTHING was
 *                 written and the caller must re-run this refresh once the
 *                 build settles.
 *  - `superseded` — a LATER refresh of this same object started while this
 *                 one was fetching ({@link BuildForObjectOptions.isSuperseded});
 *                 NOTHING was written and there is nothing to re-queue, because
 *                 that newer refresh re-reads the catalog and so covers this. */
export type BuildForObjectOutcome =
  | 'indexed' | 'absent' | 'failed' | 'deferred' | 'superseded';

/** Re-pull a single object's body from the catalog and REPLACE its index
 *  records. Fetches before it invalidates — see the module header. Never
 *  rejects on a fetch failure; reports it as `'failed'`. */
export async function buildForObject(
  options: BuildForObjectOptions,
  index: IdentifierIndex,
): Promise<BuildForObjectOutcome> {
  const sqlEngine = databaseEngineIdToSqlEngine(options.engineId);
  if (!sqlEngine) return 'failed';

  const isProcedural =
    options.objectKind === 'procedure' || options.objectKind === 'trigger';
  let body: string | null;
  try {
    body = await (isProcedural ? fetchProceduralObjectBody : fetchObjectBody)({
      connectionId: options.connectionId,
      database: options.database,
      engineId: options.engineId,
      objectKind: options.objectKind,
      objectName: options.objectName,
    });
  } catch {
    return 'failed';
  }

  // NAV-5: the connection may have been torn down (disconnect) while the
  // body fetch above was in flight. Touching the index now would write into a
  // detached/cleared one. Drop the in-flight result instead.
  if (options.isStale?.()) return 'failed';

  // …and a connection-wide build may have STARTED while it was in flight (the
  // user retrying a failed one, which empties the index first). Recording now
  // would leave the build to record the same object a second time — re-queue
  // instead of writing.
  if (options.isBuilding?.()) return 'deferred';

  // …and a LATER refresh of this same object may have started while this fetch
  // was in flight, in which case this body is the older statement's.
  if (options.isSuperseded?.()) return 'superseded';

  // Synchronous swap from here on — no await between dropping the old
  // records and recording the new ones, so a concurrent find-references never
  // reads an empty window.
  index.invalidate(options.objectKind, options.objectName);
  if (body === null) return 'absent';

  const listing: PersistedObjectListing = {
    objectKind: options.objectKind,
    objectName: options.objectName,
    bodyText: body,
    parseOptions: { engine: sqlEngine },
  };

  switch (options.objectKind) {
    case 'view':
      indexViewListing(listing, index);
      return 'indexed';
    case 'inline-tvf':
      indexInlineTvfListing(listing, index);
      return 'indexed';
    case 'scalar-function':
      indexScalarFunctionListing(listing, index);
      return 'indexed';
    case 'default-constraint':
      indexDefaultConstraintListing(listing, index);
      return 'indexed';
    case 'check-constraint':
      indexCheckConstraintListing(listing, index);
      return 'indexed';
    case 'procedure':
    case 'trigger':
      // Procedural bodies are fetched via `fetchProceduralObjectBody` (above)
      // and indexed through the same orchestrator the connection-wide builder
      // uses, so a CREATE/ALTER PROCEDURE|TRIGGER stays consistent with the
      // initial build instead of leaving stale/dropped procedural refs.
      indexProceduralListing(listing, index);
      return 'indexed';
  }
}
